import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid, siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { contentStyles, paragraphExtraSmall } from '@helpers/typography';
import SvgChevronDown from '@img/icons/chevron-down.svg';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import { RegionSelectorStyles } from '@stories/Components/Misc/RegionSelector/RegionSelector.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';
const Container = styled.footer `
  position: relative;
  z-index: 8;
  padding: 44px 0;
  background-color: ${brand.black};

  &[data-variant='reduced'] {
    padding: ${fluid(32, 40)} 0;
  }
`;
const Wrapper = styled.div `
  ${siteWide};
`;
const Inner = styled.div `
  background-color: ${brand.blue.tint};
  padding: ${fluid(25, 50)} ${fluid(25, 80)};
  border-radius: ${fluid(25, 50)};
  position: relative;

  @media ${from(Device.DesktopSmall)} {
    ${baseGrid};
    gap: ${fluid(16, 32)};
  }

  ${Container}[data-variant='reduced'] & {
    padding: 0;
    background-color: transparent;
    row-gap: 0;
  }
`;
const BackToTopBtn = styled.button `
  ${transition('background-color')};
  position: absolute;
  top: ${fluid(40, 50)};
  right: ${fluid(25, 70)};
  width: 40px;
  height: 40px;
  border: none;
  background-color: ${brand.black};
  border-radius: 4000000px;
  display: grid;
  place-content: center;

  &:hover,
  &:focus {
    background-color: ${brand.green.base};
  }

  svg {
    display: block;
    color: ${brand.white};
    transform: rotate(-180deg);
    width: 16px;
  }
`;
const TopLeftArea = styled.div `
  grid-column: span 6;

  ${Container}[data-variant='reduced'] & {
    grid-column: span 12;
  }
`;
const TopRightArea = styled.div `
  grid-column: 7 / span 6;
`;
const BottomLeftArea = styled.div `
  grid-column: span 6;
`;
const BottomRightArea = styled.div `
  grid-column: 7 / span 6;
  align-self: end;

  ${Container}[data-variant='reduced'] & {
    grid-column: span 12;
  }
`;
const HalfSplit = styled.div `
  @media ${from(Device.DesktopSmall)} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${fluid(16, 32)};
  }

  ${Container}[data-variant='reduced'] & {
    display: flex;
  }
`;
const LogoWrapper = styled.a `
  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }
  svg {
    color: ${brand.black};
    height: ${fluid(56, 68)};

    ${Container}[data-variant='reduced'] & {
      color: ${brand.white};
    }
  }

  ${Container}[data-variant='reduced'] & {
    margin-left: auto;
    margin-right: auto;
  }
`;
const LinkListBlock = styled(m.div) `
  position: relative;

  @media ${until(Device.TabletLarge)} {
    border-bottom: 1px solid ${brand.black};
  }

  @media ${until(Device.DesktopSmall)} {
    margin: 40px 0;
  }
`;
const LinkListTitle = styled.button `
  padding: 15px 0;
  background-color: transparent;
  border: none;
  width: 100%;
  text-align: left;
  margin-bottom: 0;

  @media ${until(Device.TabletLarge)} {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }

  @media ${from(Device.TabletLarge)} {
    padding: 5px 0;
  }

  ${LinkListBlock}:first-child & {
    @media ${until(Device.TabletLarge)} {
      border-top: none;
    }
  }
`;
const LinkListTitleText = styled.span `
  ${fonts.montserrat.medium};
  font-size: 15px;
  line-height: 21px;
  margin-bottom: 10px;

  @media ${until(Device.TabletLarge)} {
    margin-bottom: 0;
  }
`;
const ChevHolder = styled(m.div) `
  transform: rotate(0deg);
  transform-origin: center;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const SvgChevron = styled(SvgChevronDown) `
  width: 12px;
  color: ${brand.black};
`;
const LinkList = styled(m.div) `
  @media ${until(Device.TabletLarge)} {
    overflow: hidden;
  }

  @media ${from(Device.TabletLarge)} {
    height: auto !important;
  }
`;
const LinkListInner = styled.div ``;
const Link = styled.a `
  ${fonts.montserrat.regular};
  font-size: 15px;
  line-height: 21px;
  display: block;
  color: ${brand.black};
  text-decoration: none;
  padding: 3px 0;

  @media ${until(Device.TabletLarge)} {
    padding: 8px 0;
  }

  &:hover,
  &:focus {
    text-decoration: underline;
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }
`;
const FooterContactArea = styled.div `
  padding-bottom: 40px;

  ${ButtonStyles.Button} {
    --buttonHeight: 50px;
  }

  @media ${until(Device.TabletLarge)} {
    ${ButtonStyles.Button} {
      width: 100%;
      justify-content: center;
    }
  }
`;
const FooterContactTitle = styled.h4 `
  ${fonts.lustria.regular};
  font-size: 17px;
  line-height: 23px;
  margin-bottom: 20px;

  @media ${until(Device.TabletLarge)} {
    text-align: center;
  }
`;
const FooterTrustpilotArea = styled.div `
  padding-bottom: 40px;
  display: flex;

  @media ${until(Device.TabletLarge)} {
    justify-content: center;
  }
`;
const FooterSocialArea = styled.div ``;
const FooterSocialTitle = styled.h4 `
  ${fonts.lustria.regular};
  font-size: 16px;
  line-height: 22px;
  margin-bottom: 20px;

  @media ${until(Device.TabletLarge)} {
    text-align: center;
  }
`;
const FooterSocialLinks = styled.div `
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;

  @media ${from(Device.TabletLarge)} {
    justify-content: flex-start;
  }
`;
const SocialLink = styled.a `
  text-decoration: none;
  color: var(--textColor);
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.6;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  svg {
    height: 20px;
  }
`;
const CopyrightList = styled.ul `
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  column-gap: 40px;
  row-gap: 20px;
  padding: 20px 0;

  @media ${until(Device.TabletLarge)} {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }

  li {
    position: relative;

    &:last-child {
      &:after {
        content: none;
      }
    }

    ${Container}[data-variant='reduced'] & {
      @media ${until(Device.TabletLarge)} {
        flex: 1 1 100%;
        max-width: calc(50% - 8px);
      }
    }

    @media ${from(Device.TabletLarge)} {
      &:after {
        content: '';
        position: absolute;
        top: 50%;
        right: -22px;
        transform: translateY(-50%);
        width: 5px;
        height: 5px;
        background-color: ${brand.black};
        border-radius: 50%;

        ${Container}[data-variant='reduced'] & {
          background-color: ${brand.white};
        }
      }
    }
  }

  ${Container}[data-variant='reduced'] & {
    padding: ${fluid(26, 33)} 0 0;
    text-align: center;
    justify-content: center;

    @media ${until(Device.TabletLarge)} {
      gap: 16px;
      flex-direction: row;
    }
  }
`;
const CopyrightLink = styled.a `
  ${fonts.montserrat.medium};

  font-size: 13px;
  line-height: 19px;
  color: ${brand.black};
  text-decoration: none;

  &:focus {
    outline: none;
  }

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-color: var(--textColor);
  }

  ${Container}[data-variant='reduced'] & {
    color: ${brand.white};
  }
`;
const BaseArea = styled.div `
  grid-column: span 12;

  ${contentStyles};
  text-align: center;

  p {
    ${paragraphExtraSmall};
  }
`;
const BaseContent = styled.div ``;
const RegionArea = styled.div `
  padding: 0;
  text-align: center;
  grid-column: span 12;

  ${RegionSelectorStyles.RegionButton} {
    padding: 6px 8px;
    background-color: ${brand.white};
    border-radius: 8px;
  }

  ${RegionSelectorStyles.RegionDropDown} {
    top: auto;
    bottom: calc(100% + 20px);
    left: 50%;
    right: auto;
    transform: translateX(-50%);

    &:after {
        border: solid transparent;
        content: "";
        height: 0;
        width: 0;
        position: absolute;
        pointer-events: none;
        border-color: rgba(136, 183, 213, 0);
        border-top-color: ${brand.white};
        border-width: 10px;
        margin-left: -10px;
        transform: scaleX(0.6);
        bottom: auto;
        top: 100%;
        left: 50%;
        
      }
  }
`;
const FooterStyles = {
    BackToTopBtn,
    BaseArea,
    BaseContent,
    RegionArea,
    BottomLeftArea,
    BottomRightArea,
    ChevHolder,
    Container,
    CopyrightLink,
    CopyrightList,
    FooterContactArea,
    FooterTrustpilotArea,
    FooterContactTitle,
    FooterSocialArea,
    FooterSocialLinks,
    FooterSocialTitle,
    HalfSplit,
    Inner,
    Link,
    LinkList,
    LinkListBlock,
    LinkListInner,
    LinkListTitle,
    LinkListTitleText,
    LogoWrapper,
    SocialLink,
    SvgChevron,
    TopLeftArea,
    TopRightArea,
    Wrapper,
};
export default FooterStyles;
